import { z } from 'zod';
import { SchemaFormDefinition } from '@mews-commander/form';
import { TextKey } from '@mews-commander/enums';
import { compactArray } from '@mews/fp';
import { lt } from '@mews/framework';
import { UserStatus } from '@mews-commander/data-access';
import { Option } from '@mews-commander/types';
import { createOption } from '@mews-commander/ui';
import { useUserContext } from '../../context';
import { useDisplaySourceColumn } from '../../hooks/useDisplaySourceColumn';
import { UserType } from '../../types';

enum FilterValueKey {
    Name = 'name',
    SuperiorName = 'superiorName',
    Roles = 'roles',
    IsAdmin = 'isAdmin',
    Status = 'status',
    Source = 'source',
    Reset2fa = 'reset2fa',
}

export enum UserSource {
    Idp = 'idp',
    Mews = 'mews',
    All = 'all',
}

export const filterSchema = z.object({
    [FilterValueKey.Name]: z.string().optional(),
    [FilterValueKey.SuperiorName]: z.string().optional(),
    [FilterValueKey.Roles]: z.string().optional(),
    [FilterValueKey.IsAdmin]: z.boolean().optional(),
    [FilterValueKey.Status]: z.array(z.string()).optional(),
    [FilterValueKey.Source]: z.nativeEnum(UserSource),
    [FilterValueKey.Reset2fa]: z.boolean().optional(),
});

export type FilterValues = z.infer<typeof filterSchema>;

export const defaultFilterValues: FilterValues = {
    [FilterValueKey.Name]: '',
    [FilterValueKey.SuperiorName]: '',
    [FilterValueKey.Roles]: '',
    [FilterValueKey.Source]: UserSource.All,
};

export interface WithFilterValues {
    filterValues: FilterValues;
}

const userStatusOptions: Option[] = [
    {
        value: UserStatus.Active,
        label: lt(TextKey.Active),
    },
    {
        value: UserStatus.Pending,
        label: lt(TextKey.Pending),
    },
    {
        value: UserStatus.Disabled,
        label: lt(TextKey.Deactivated),
    },
].map(createOption);

const userSourceOptions: Option[] = [
    {
        value: UserSource.All,
        label: lt(TextKey.All),
    },
    {
        value: UserSource.Idp,
        label: lt(TextKey.IDP),
    },
    {
        value: UserSource.Mews,
        label: lt(TextKey.Mews),
    },
].map(createOption);

export const useFormDefinition = (): SchemaFormDefinition<typeof filterSchema> => {
    const displaySourceFilter = useDisplaySourceColumn();
    const { userType } = useUserContext();

    return {
        fields: {
            [FilterValueKey.Name]: {
                label: lt(TextKey.Name),
            },
            [FilterValueKey.SuperiorName]: {
                label: lt(TextKey.SuperiorEmployee),
            },
            [FilterValueKey.Roles]: {
                label: lt(TextKey.Roles),
            },
            [FilterValueKey.IsAdmin]: {
                label: lt(TextKey.IsAdmin),
            },
            [FilterValueKey.Status]: {
                label: lt(TextKey.Status),
                field: { type: 'select', options: userStatusOptions },
            },
            [FilterValueKey.Source]: {
                label: lt(TextKey.Source),
                field: { type: 'select', options: userSourceOptions },
            },
            [FilterValueKey.Reset2fa]: {
                label: lt(TextKey.ResetTwoFactorShort),
                field: { type: 'checkbox' },
            },
        },
        renderFields: compactArray([
            FilterValueKey.Name,
            userType === UserType.Properties ? null : FilterValueKey.SuperiorName,
            userType === UserType.Portfolio ? null : FilterValueKey.Roles,
            userType === UserType.Portfolio ? null : FilterValueKey.IsAdmin,
            FilterValueKey.Status,
            displaySourceFilter ? FilterValueKey.Source : null,
            FilterValueKey.Reset2fa,
        ]),
    };
};
